import { useLoginStore } from '@/login/stores/useLoginStore';
import { ResgateManager } from '@/newApi/services/ResgateManager';
import { assertNotResErr } from '@/api/helpers';
import type { UseMutationOptions, QueryOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';
import {
    ALL_SCOPES,
    type ApiKey,
    type ApiKeyEnvironment,
    type CreatedApiKey,
    type ScopeName,
} from '@sysarb/sysarb-types/services/v1/public-api';

export { ALL_SCOPES };
export type { ApiKey, ApiKeyEnvironment, CreatedApiKey, ScopeName };

export type ApiKeyCreateInput = {
    name: string;
    scopes: readonly ScopeName[];
    expiresAt?: Date;
};

export type ApiKeyRevokeInput = {
    reason: string;
};

export type ApiKeyUpdateInput = {
    id: string;
    scopes: readonly ScopeName[];
};

type ApiKeysOptions = {
    queries: {
        list: () => QueryOptions<{ keys: ApiKey[] }, Error>;
        get: (id: string) => QueryOptions<ApiKey, Error>;
    };
    mutations: {
        create: () => UseMutationOptions<CreatedApiKey, Error, ApiKeyCreateInput>;
        update: () => UseMutationOptions<boolean, Error, ApiKeyUpdateInput>;
        revoke: () => UseMutationOptions<boolean, Error, { id: string; reason: string }>;
    };
};

function collectionRid(instanceTopic: string | null): string {
    if (instanceTopic == null) {
        throw new Error('Invalid subdomain');
    }
    return `v1.${instanceTopic}.api-keys`;
}

function itemRid(instanceTopic: string | null, id: string): string {
    if (instanceTopic == null) {
        throw new Error('Invalid subdomain');
    }
    return `v1.${instanceTopic}.api-keys.${id}`;
}

export const apiKeys: ApiKeysOptions = {
    queries: {
        list: (): QueryOptions<{ keys: ApiKey[] }, Error> => {
            const { instanceTopic } = useLoginStore();
            const rid = collectionRid(instanceTopic);
            return queryOptions<{ keys: ApiKey[] }, Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: rid,
                },
                queryKey: ['api-keys', instanceTopic, 'list', rid],
                queryFn: async (): Promise<{ keys: ApiKey[] }> =>
                    assertNotResErr(await ResgateManager.model<{ keys: ApiKey[] }>(rid)),
            });
        },
        get: (id: string): QueryOptions<ApiKey, Error> => {
            const { instanceTopic } = useLoginStore();
            const rid = itemRid(instanceTopic, id);
            return queryOptions<ApiKey, Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: rid,
                },
                queryKey: ['api-keys', instanceTopic, 'detail', id, rid],
                queryFn: async (): Promise<ApiKey> => assertNotResErr(await ResgateManager.model<ApiKey>(rid)),
            });
        },
    },
    mutations: {
        create: (): UseMutationOptions<CreatedApiKey, Error, ApiKeyCreateInput> => {
            const { instanceTopic } = useLoginStore();
            const rid = collectionRid(instanceTopic);
            return {
                mutationFn: async (payload: ApiKeyCreateInput): Promise<CreatedApiKey> => {
                    const resgate = ResgateManager.getInstance();
                    return assertNotResErr(await resgate.call<CreatedApiKey>(rid, 'create', payload));
                },
            };
        },
        update: (): UseMutationOptions<boolean, Error, ApiKeyUpdateInput> => {
            const { instanceTopic } = useLoginStore();
            return {
                mutationFn: async (payload: ApiKeyUpdateInput): Promise<boolean> => {
                    const rid = itemRid(instanceTopic, payload.id);
                    const resgate = ResgateManager.getInstance();
                    assertNotResErr(await resgate.call<void>(rid, 'update', { scopes: payload.scopes }));
                    return true;
                },
            };
        },
        revoke: (): UseMutationOptions<boolean, Error, { id: string; reason: string }> => {
            const { instanceTopic } = useLoginStore();
            return {
                mutationFn: async (payload: { id: string; reason: string }): Promise<boolean> => {
                    const rid = itemRid(instanceTopic, payload.id);
                    const resgate = ResgateManager.getInstance();
                    const body: ApiKeyRevokeInput = { reason: payload.reason };
                    assertNotResErr(await resgate.call<void>(rid, 'revoke', body));
                    return true;
                },
            };
        },
    },
};
